import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Car, Plus, X } from 'lucide-react'

import { ApiError } from '@/api/client'
import { instrumentsApi, locationsApi, studentsApi, teachersApi } from '@/api/endpoints'
import type { SkillLevel, Student, StudentStatus } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { isTeacher } from '@/auth/permissions'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { MultiSelect, SelectField, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { SKILL_LEVEL_LABELS, STUDENT_STATUS_LABELS } from '@/lib/dictionaries'

interface Props {
  open: boolean
  onClose: () => void
  student: Student | null
}

interface FormState {
  location_id: string
  first_name: string
  last_name: string
  birth_date: string
  phone: string
  email: string
  guardian_name: string
  guardian_phone: string
  guardian_email: string
  guardian2_name: string
  guardian2_phone: string
  guardian2_email: string
  is_traveling: boolean
  address: string
  instrument_ids: string[]
  teacher_id: string
  level: SkillLevel
  status: StudentStatus
  enrolled_on: string
}

const EMPTY: FormState = {
  location_id: '',
  first_name: '',
  last_name: '',
  birth_date: '',
  phone: '',
  email: '',
  guardian_name: '',
  guardian_phone: '',
  guardian_email: '',
  guardian2_name: '',
  guardian2_phone: '',
  guardian2_email: '',
  is_traveling: false,
  address: '',
  instrument_ids: [],
  teacher_id: '',
  level: 'BEGINNER',
  status: 'ACTIVE',
  enrolled_on: '',
}

/** Pusty tekst zamieniamy na `null` — API odróżnia „brak” od „pusty ciąg”. */
const orNull = (value: string) => value.trim() || null

/**
 * Dwa komplety pól opiekuna, wypisane wprost.
 *
 * Baza trzyma opiekunów w osobnych kolumnach, a nie w tabeli podrzędnej —
 * dwóch wystarcza na rozdzielonych rodziców i nie wymaga osobnego formularza.
 * Ta tablica pozwala renderować oba bloki jedną pętlą zamiast kopiować JSX.
 */
const OPIEKUNOWIE = [
  {
    tytul: 'Opiekun',
    // Biernik wypisany osobno — złożenie „Usuń ” z tytułem dałoby mianownik.
    usun: 'Usuń opiekuna',
    dodaj: 'Dodaj opiekuna',
    name: 'guardian_name',
    phone: 'guardian_phone',
    email: 'guardian_email',
  },
  {
    tytul: 'Drugi opiekun',
    usun: 'Usuń drugiego opiekuna',
    dodaj: 'Dodaj drugiego opiekuna',
    name: 'guardian2_name',
    phone: 'guardian2_phone',
    email: 'guardian2_email',
  },
] as const satisfies readonly {
  tytul: string
  usun: string
  dodaj: string
  name: keyof FormState
  phone: keyof FormState
  email: keyof FormState
}[]

/** Ilu opiekunów ma już dane — tylu bloków nie da się ukryć przy otwarciu. */
function policzWypelnionych(form: FormState): number {
  const wypelniony = (opiekun: (typeof OPIEKUNOWIE)[number]) =>
    Boolean(form[opiekun.name] || form[opiekun.phone] || form[opiekun.email])

  // Liczymy do ostatniego wypełnionego, a nie sumę: gdyby w bazie siedział
  // sam drugi opiekun, pierwszy blok i tak musi być widoczny.
  const ostatni = OPIEKUNOWIE.map(wypelniony).lastIndexOf(true)
  return ostatni + 1
}

export function StudentFormModal({ open, onClose, student }: Props) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { notifySuccess } = useToast()
  const isEdit = student !== null
  const teacherMode = isTeacher(user)

  const [form, setForm] = useState<FormState>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  // Ile bloków opiekuna pokazujemy. Zero przy nowym uczniu: większość zapisów
  // to dorośli albo uczniowie z własnym telefonem, a sześć pustych pól
  // wydłużało formularz o ekran.
  const [liczbaOpiekunow, setLiczbaOpiekunow] = useState(0)

  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
    enabled: open,
  })
  const instrumentsQuery = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentsApi.list(),
    enabled: open,
  })
  const teachersQuery = useQuery({
    queryKey: ['teachers', 'picker', form.location_id],
    queryFn: () => teachersApi.list({ size: 200, location_id: form.location_id || undefined }),
    enabled: open && !teacherMode,
  })

  const locations = locationsQuery.data?.items ?? []

  /**
   * Czy w formularzu jest pole oddziału.
   *
   * Administracja wybiera zawsze. Nauczyciel — przy zakładaniu ucznia i tylko
   * wtedy, gdy uczy w kilku oddziałach: przy jednym nie ma czego wybierać,
   * a serwer i tak podstawia ten sam. Przy edycji pola nie ma, bo przeniesienie
   * ucznia między oddziałami serwer odrzuca („robi administrator”) — kontrolka
   * prowadziłaby wprost do odmowy.
   *
   * Lista oddziałów przychodzi z serwera już zawężona do konta, więc nauczyciel
   * widzi wyłącznie swoje.
   */
  const wyborOddzialu = teacherMode ? !isEdit && locations.length > 1 : true

  /**
   * Lista prowadzących do wyboru.
   *
   * Po zmianie oddziału obecny prowadzący potrafi z niej wypaść — uczy gdzie
   * indziej. Dopisujemy go wtedy ręcznie, żeby pole nie wyglądało na puste,
   * choć uczeń nadal go ma. Zniknięcie nazwiska z ekranu czyta się jak
   * skasowanie przypisania.
   */
  const prowadzacyOddzialu = teachersQuery.data?.items ?? []
  const uczyWOddziale =
    student?.teacher == null ||
    prowadzacyOddzialu.some((item) => item.user.id === student.teacher?.id)
  const opcjeProwadzacych = [
    ...prowadzacyOddzialu.map((item) => ({ value: item.user.id, label: item.user.full_name })),
    ...(uczyWOddziale || student?.teacher == null
      ? []
      : [{ value: student.teacher.id, label: `${student.teacher.full_name} (inny oddział)` }]),
  ]

  useEffect(() => {
    if (!open) return
    setError(null)
    setFieldErrors({})
    const stan: FormState = student
      ? {
          location_id: student.location.id,
          first_name: student.first_name,
          last_name: student.last_name,
          birth_date: student.birth_date ?? '',
          phone: student.phone ?? '',
          email: student.email ?? '',
          guardian_name: student.guardian_name ?? '',
          guardian_phone: student.guardian_phone ?? '',
          guardian_email: student.guardian_email ?? '',
          guardian2_name: student.guardian2_name ?? '',
          guardian2_phone: student.guardian2_phone ?? '',
          guardian2_email: student.guardian2_email ?? '',
          is_traveling: student.is_traveling,
          address: student.address ?? '',
          instrument_ids: student.instruments.map((item) => item.id),
          teacher_id: student.teacher?.id ?? '',
          level: student.level,
          status: student.status,
          enrolled_on: student.enrolled_on ?? '',
        }
      : {
          ...EMPTY,
          location_id: locations[0]?.id ?? '',
          enrolled_on: new Date().toISOString().slice(0, 10),
        }

    setForm(stan)
    setLiczbaOpiekunow(policzWypelnionych(stan))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student, locations.length])

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  /**
   * Usuwa blok opiekuna razem z jego danymi.
   *
   * Skasowanie pierwszego przy dwóch wypełnionych przesuwa drugiego na jego
   * miejsce — inaczej zostałaby luka: pusty „Opiekun” i wypełniony „Drugi”.
   */
  const usunOpiekuna = (indeks: number) => {
    setForm((current) => {
      const bez = { ...current }
      if (indeks === 0 && liczbaOpiekunow === 2) {
        bez.guardian_name = current.guardian2_name
        bez.guardian_phone = current.guardian2_phone
        bez.guardian_email = current.guardian2_email
        bez.guardian2_name = ''
        bez.guardian2_phone = ''
        bez.guardian2_email = ''
        return bez
      }
      const opiekun = OPIEKUNOWIE[indeks]
      bez[opiekun.name] = ''
      bez[opiekun.phone] = ''
      bez[opiekun.email] = ''
      return bez
    })
    setLiczbaOpiekunow((current) => current - 1)
  }

  const mutation = useMutation({
    mutationFn: () => {
      const base = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        birth_date: orNull(form.birth_date),
        phone: orNull(form.phone),
        email: orNull(form.email),
        guardian_name: orNull(form.guardian_name),
        guardian_phone: orNull(form.guardian_phone),
        guardian_email: orNull(form.guardian_email),
        guardian2_name: orNull(form.guardian2_name),
        guardian2_phone: orNull(form.guardian2_phone),
        guardian2_email: orNull(form.guardian2_email),
        is_traveling: form.is_traveling,
        address: orNull(form.address),
        instrument_ids: form.instrument_ids,
        level: form.level,
        status: form.status,
        enrolled_on: orNull(form.enrolled_on),
      }

      if (isEdit) {
        // Nauczyciel nie może zmieniać prowadzącego ani oddziału — te pola
        // pomijamy, żeby nie wysyłać żądania skazanego na 403.
        //
        // Prowadzącego wysyłamy wyłącznie wtedy, gdy ktoś go faktycznie
        // zmienił. Przy przenoszeniu ucznia do innego oddziału niezmienione
        // pole oznaczałoby „zostaw jak jest”, a wysłane wprost trafiłoby na
        // sprawdzenie, czy nauczyciel uczy w nowym oddziale — i odmowę przy
        // kimś, kogo nikt nie ruszał.
        const prowadzacyZmieniony = (form.teacher_id || null) !== (student.teacher?.id ?? null)
        return studentsApi.update(student.id, {
          ...base,
          ...(teacherMode ? {} : { location_id: form.location_id }),
          ...(teacherMode || !prowadzacyZmieniony ? {} : { teacher_id: form.teacher_id || null }),
        })
      }
      return studentsApi.create({
        ...base,
        location_id: form.location_id,
        ...(teacherMode ? {} : { teacher_id: form.teacher_id || null }),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['students'] })
      notifySuccess(isEdit ? 'Zapisano dane ucznia.' : 'Uczeń został dodany.')
      onClose()
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setError(caught.message)
        setFieldErrors(caught.fieldErrors)
      } else {
        setError('Nie udało się zapisać ucznia.')
      }
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    mutation.mutate()
  }

  // Lustro walidacji z backendu — kontakt do drugiego opiekuna też wystarcza.
  const hasContact = Boolean(
    form.phone.trim() ||
    form.email.trim() ||
    form.guardian_phone.trim() ||
    form.guardian_email.trim() ||
    form.guardian2_phone.trim() ||
    form.guardian2_email.trim(),
  )
  // Adres przy dojeździe pilnuje też serwer; blokada przycisku oszczędza
  // odbicie od walidacji po wypełnieniu całego formularza.
  const canSubmit =
    (!form.is_traveling || form.address.trim() !== '') &&
    form.first_name.trim() !== '' &&
    form.last_name.trim() !== '' &&
    hasContact &&
    (isEdit || form.location_id !== '')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edytuj dane ucznia' : 'Nowy uczeń'}
      description={
        // Opis niesie to, czego nie da się zmienić, a co warto wiedzieć:
        // prowadzącego i oddział, gdy formularz otwiera nauczyciel.
        // Wcześniej stały tu nieaktywne pola.
        teacherMode
          ? isEdit
            ? 'Zmianę prowadzącego i oddziału wykonuje administracja.'
            : 'Uczeń zostanie przypisany do Ciebie jako prowadzącego.'
          : isEdit
            ? 'Zmiana oddziału nie rusza już zaplanowanych zajęć — sprawdź grafik po zapisaniu.'
            : 'Dane kontaktowe są potrzebne do powiadomień o odwołanych zajęciach.'
      }
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            form="student-form"
            isLoading={mutation.isPending}
            disabled={!canSubmit}
          >
            {isEdit ? 'Zapisz zmiany' : 'Dodaj ucznia'}
          </Button>
        </>
      }
    >
      <form id="student-form" onSubmit={handleSubmit} className="space-y-6" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <fieldset className="space-y-5">
          <legend className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
            Dane ucznia
          </legend>

          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Imię"
              value={form.first_name}
              onChange={(event) => update('first_name', event.target.value)}
              error={fieldErrors.first_name}
              required
              autoFocus
            />
            <TextField
              label="Nazwisko"
              value={form.last_name}
              onChange={(event) => update('last_name', event.target.value)}
              error={fieldErrors.last_name}
              required
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Data urodzenia"
              type="date"
              value={form.birth_date}
              onChange={(event) => update('birth_date', event.target.value)}
              error={fieldErrors.birth_date}
            />
            <TextField
              label="Data zapisu"
              type="date"
              value={form.enrolled_on}
              onChange={(event) => update('enrolled_on', event.target.value)}
              error={fieldErrors.enrolled_on}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Telefon ucznia"
              type="tel"
              value={form.phone}
              onChange={(event) => update('phone', event.target.value)}
              error={fieldErrors.phone}
            />
            <TextField
              label="E-mail ucznia"
              type="email"
              value={form.email}
              onChange={(event) => update('email', event.target.value)}
              error={fieldErrors.email}
            />
          </div>

          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-surface-container-low px-4 py-2.5 text-sm font-semibold">
            <input
              type="checkbox"
              checked={form.is_traveling}
              onChange={(event) => update('is_traveling', event.target.checked)}
              className="h-4 w-4 rounded border-outline/40 text-primary focus:ring-primary"
            />
            <Car size={16} aria-hidden="true" />
            Zajęcia z dojazdem
          </label>

          {form.is_traveling && (
            <TextField
              label="Adres ucznia"
              value={form.address}
              onChange={(event) => update('address', event.target.value)}
              error={fieldErrors.address}
              hint="Nauczyciel dojeżdża pod ten adres. Takie zajęcia nie zajmują sali i nie pojawiają się w grafiku sal."
              placeholder="ul. Sienkiewicza 4/2, Bytom"
              required
            />
          )}
        </fieldset>

        <fieldset className="space-y-5 border-t border-outline/10">
          <legend className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
            Opiekunowie
          </legend>

          {OPIEKUNOWIE.slice(0, liczbaOpiekunow).map((opiekun, indeks) => (
            <div
              key={opiekun.name}
              className={cn('space-y-5', indeks > 0 && 'border-t border-outline/10 pt-5')}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-on-surface">{opiekun.tytul}</p>
                <button
                  type="button"
                  onClick={() => usunOpiekuna(indeks)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold text-on-surface-variant transition-colors hover:bg-error-container hover:text-on-error-container"
                >
                  <X size={14} aria-hidden="true" />
                  {opiekun.usun}
                </button>
              </div>

              <TextField
                label="Imię i nazwisko"
                value={form[opiekun.name]}
                onChange={(event) => update(opiekun.name, event.target.value)}
                error={fieldErrors[opiekun.name]}
              />

              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  label="Telefon"
                  type="tel"
                  value={form[opiekun.phone]}
                  onChange={(event) => update(opiekun.phone, event.target.value)}
                  error={fieldErrors[opiekun.phone]}
                />
                <TextField
                  label="E-mail"
                  type="email"
                  value={form[opiekun.email]}
                  onChange={(event) => update(opiekun.email, event.target.value)}
                  error={fieldErrors[opiekun.email]}
                />
              </div>
            </div>
          ))}

          {liczbaOpiekunow < OPIEKUNOWIE.length && (
            <Button
              variant="outline"
              size="sm"
              icon={<Plus size={16} />}
              onClick={() => setLiczbaOpiekunow((current) => current + 1)}
            >
              {OPIEKUNOWIE[liczbaOpiekunow].dodaj}
            </Button>
          )}

          {liczbaOpiekunow === 0 && (
            <p className="text-xs text-on-surface-variant">
              Uczeń pełnoletni albo z własnym telefonem nie potrzebuje opiekuna.
            </p>
          )}

          {!hasContact && (
            <Alert tone="warning">
              Podaj przynajmniej jeden kontakt — telefon lub e-mail ucznia albo opiekuna. Bez tego
              nie da się powiadomić o odwołanych zajęciach.
            </Alert>
          )}
        </fieldset>

        <fieldset className="space-y-5 border-t border-outline/10">
          <legend className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
            Nauka
          </legend>

          {/* Oddział zmienia się tu jak każde inne pole. Kiedy pole widać —
              patrz `wyborOddzialu`. */}
          {wyborOddzialu && (
            <SelectField
              label="Oddział"
              value={form.location_id}
              onChange={(event) => update('location_id', event.target.value)}
              options={locations.map((location) => ({
                value: location.id,
                label: location.name,
              }))}
              placeholder={locationsQuery.isPending ? 'Wczytywanie…' : 'Wybierz oddział'}
              hint={
                teacherMode
                  ? 'Uczysz w kilku oddziałach — wskaż, do którego zapisujemy ucznia. Później przeniesie go administrator.'
                  : undefined
              }
              error={fieldErrors.location_id}
              required
            />
          )}

          <MultiSelect
            label="Instrumenty"
            hint="Zaznacz wszystkie, na których uczeń gra. Przy jednym system podstawi go automatycznie w formularzu lekcji."
            options={(instrumentsQuery.data ?? []).map((instrument) => ({
              value: instrument.id,
              label: instrument.name,
            }))}
            selected={form.instrument_ids}
            onChange={(values) => update('instrument_ids', values)}
            error={fieldErrors.instrument_ids}
            placeholder="Wybierz instrumenty"
            emptyMessage="Brak instrumentów w słowniku."
          />

          {/* Nauczyciel nie zmienia prowadzącego, więc nie widzi tego pola.
              Że uczeń trafi do niego, mówi opis okna. */}
          {!teacherMode && (
            <SelectField
              label="Prowadzący"
              value={form.teacher_id}
              onChange={(event) => update('teacher_id', event.target.value)}
              options={opcjeProwadzacych}
              placeholder="Nie przypisano"
              hint={
                isEdit && student?.teacher && !uczyWOddziale
                  ? `${student.teacher.full_name} nie uczy w wybranym oddziale — zostaje przypisany, dopóki nie wskażesz kogoś innego.`
                  : undefined
              }
              error={fieldErrors.teacher_id}
            />
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <SelectField
              label="Poziom"
              value={form.level}
              onChange={(event) => update('level', event.target.value as SkillLevel)}
              options={Object.entries(SKILL_LEVEL_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
            <SelectField
              label="Status"
              value={form.status}
              onChange={(event) => update('status', event.target.value as StudentStatus)}
              options={Object.entries(STUDENT_STATUS_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </div>
        </fieldset>
      </form>
    </Modal>
  )
}
